// IndexedDB-backed image cache — fetches image bytes once and re-uses
// them across page reloads on the iPad kiosk.
//
// The ingress in front of `/api/files/*` forces `Cache-Control: no-store`,
// which defeats the browser HTTP cache. Without a persistent store,
// every hard refresh (or a kiosk that sleeps and wakes) re-downloads
// every treatment / crew / cabin photo. This module puts them in
// IndexedDB as Blobs — an eviction-resistant, permission-free ~50 MB
// budget on modern iPadOS — so the next boot paints from cache while
// only the ETag validator hits the network.
//
// Usage:
//   const url = await getBlobUrl(imagePath);   // resolves to a blob:… URL
//   <img src={url} />
//
// Or via React:
//   const url = useBlobImage(imagePath, fileUrl(path));
//   <img src={url ?? fileUrl(path)} />
//
// The `useBlobImage` hook returns `null` while resolving so callers can
// hold their placeholder (or fall back to the direct `fileUrl` so the
// tile paints even before IDB responds — network + IDB race).
import { fileUrl } from "@/lib/api";

const DB_NAME = "yspa-image-cache";
const STORE = "blobs";
const DB_VERSION = 1;
// Keep entries fresh for 7 days — long enough to survive a charter,
// short enough that treatment photo updates propagate without an
// explicit invalidate. Time comes from the entry's stored `at`.
const MAX_AGE_MS = 7 * 24 * 3600_000;

let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      // Old browser / private mode — behave as a no-op cache.
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "path" });
        store.createIndex("at", "at");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }).catch((e) => { _dbPromise = null; throw e; });
  return _dbPromise;
}

async function idbGet(path) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(path);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(path, blob, etag) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const req = tx.objectStore(STORE).put({ path, blob, etag: etag || null, at: Date.now() });
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  }).catch(() => { /* quota / private mode — best-effort */ });
}

// Live in-flight requests keyed by path so parallel `getBlobUrl` calls
// for the same tile don't fire the fetch twice.
const pending = new Map();

// Live blob-URL bindings keyed by path. `URL.createObjectURL` allocates
// memory tied to the document; we keep one URL per path and reuse it
// so remounting a tile doesn't leak.
const blobUrls = new Map();

function urlForBlob(path, blob) {
  const existing = blobUrls.get(path);
  if (existing) return existing;
  try {
    const u = URL.createObjectURL(blob);
    blobUrls.set(path, u);
    return u;
  } catch { return null; }
}

/**
 * Synchronous accessor — returns the already-materialised blob: URL
 * for `path` if one has been minted this session, otherwise `null`.
 * Used by <BlobImage> to paint from RAM on first render (avoiding a
 * network round-trip via the direct `/api/files/*` URL, which the
 * ingress marks `no-store`). Never triggers a fetch; pair with
 * `getBlobUrl` or `prewarmBlobImages` to populate the cache.
 */
export function getBlobUrlSync(path) {
  if (!path) return null;
  return blobUrls.get(path) || null;
}

/**
 * Return a blob: URL for the given path, priming IDB from the network
 * if we don't already have it. Never throws — falls back silently and
 * returns `null` so the caller uses their direct fileUrl fallback.
 */
export async function getBlobUrl(path) {
  if (!path) return null;
  // In-memory hit
  if (blobUrls.has(path)) return blobUrls.get(path);
  // Race: if someone else is already fetching this path, share their promise.
  if (pending.has(path)) return pending.get(path);

  const p = (async () => {
    try {
      const cached = await idbGet(path);
      if (cached && cached.blob && (Date.now() - (cached.at || 0)) < MAX_AGE_MS) {
        return urlForBlob(path, cached.blob);
      }
      // Fetch fresh. Send If-None-Match so we short-circuit on 304 when
      // the server-set ETag is still valid (skip re-download of the
      // full bytes even if the cache is stale by TTL).
      const headers = {};
      if (cached?.etag) headers["If-None-Match"] = cached.etag;
      const resp = await fetch(fileUrl(path), { headers, credentials: "include" });
      if (resp.status === 304 && cached?.blob) {
        // Refresh the timestamp so we don't refetch immediately.
        idbPut(path, cached.blob, cached.etag);
        return urlForBlob(path, cached.blob);
      }
      if (!resp.ok) return null;
      const blob = await resp.blob();
      const etag = resp.headers.get("etag");
      idbPut(path, blob, etag);
      return urlForBlob(path, blob);
    } catch { return null; }
    finally { pending.delete(path); }
  })();
  pending.set(path, p);
  return p;
}

/** Fire-and-forget prewarm — pipes a list of paths through `getBlobUrl`
 *  so the IDB store is primed while the guest is still on the CabinView. */
export function prewarmBlobImages(paths) {
  if (!Array.isArray(paths)) return;
  for (const p of paths) if (p) getBlobUrl(p); // resolves silently
}

/** Manual invalidator — used only by admin tooling / debug console. */
export async function invalidateBlobImage(path) {
  try {
    const db = await openDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const req = tx.objectStore(STORE).delete(path);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch { /* ignore */ }
  const u = blobUrls.get(path);
  if (u) { try { URL.revokeObjectURL(u); } catch { /* ignore */ } blobUrls.delete(path); }
}
